import { formatEuros, formatPercent } from "../../../dashboard-framework/format";
import { TOTAL_COLUMN_KEY } from "../../../dashboard-framework/pivot";

export const TYPICAL_MONTH_COLUMN_KEY = "__typical";
export const TYPICAL_MONTH_COLUMN_LABEL = "Mês típico";
export const DEVIATION_MINIMUM_EUROS = 200;

const COST_TYPE_LABEL = "Despesa";
const REVENUE_TYPE_LABEL = "Receita";
const NEGLIGIBLE_TYPICAL_EUROS = 0.005;

type MonthValues = {
    columnKeys: Array<string>;
    rawByColumn: Record<string, number | null>;
};

type DeviationRow = {
    path: Array<string>;
    rawByColumn: Record<string, number | null>;
};

export type MonthlyDeviationContext = {
    currentMonthColumnKey: string | null;
    thresholdPercent: number;
};

export type MonthlyDeviation = {
    direction: "up" | "down";
    // Direção é "mais/menos dinheiro na rubrica"; impacto é o que isso vale para o resultado —
    // numa despesa gastar mais é pior, numa receita receber mais é melhor.
    impact: "better" | "worse";
    value: number;
    typicalValue: number;
    deviationEuros: number;
    deviationPercent: number;
};

const roundToCents = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    return rounded;
};

const medianOf = (values: Array<number>) => {
    const sorted = [...values].sort((first, second) => first - second);
    const middle = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
    return median;
};

const eligibleMonthValues = (monthValues: MonthValues, currentMonthColumnKey: string | null) => {
    const values: Array<number> = [];
    monthValues.columnKeys.forEach((columnKey) => {
        if (columnKey === currentMonthColumnKey) return;
        const value = monthValues.rawByColumn[columnKey];
        if (typeof value !== "number") return;
        values.push(roundToCents(value));
    });
    return values;
};

export const typicalMonthValue = (monthValues: MonthValues, currentMonthColumnKey: string | null) => {
    const values = eligibleMonthValues(monthValues, currentMonthColumnKey);
    const typicalValue = values.length === 0 ? null : roundToCents(medianOf(values));
    return typicalValue;
};

const rowTypeLabel = (path: Array<string>) => {
    if (path.includes(COST_TYPE_LABEL)) return COST_TYPE_LABEL;
    if (path.includes(REVENUE_TYPE_LABEL)) return REVENUE_TYPE_LABEL;
    return null;
};

export const monthlyDeviation = (row: DeviationRow, columnKey: string, context: MonthlyDeviationContext) => {
    const typeLabel = rowTypeLabel(row.path);
    if (!typeLabel) return null;
    if (columnKey === TOTAL_COLUMN_KEY || columnKey === TYPICAL_MONTH_COLUMN_KEY) return null;
    if (columnKey === context.currentMonthColumnKey) return null;

    const typicalValue = row.rawByColumn[TYPICAL_MONTH_COLUMN_KEY];
    if (typeof typicalValue !== "number" || Math.abs(typicalValue) < NEGLIGIBLE_TYPICAL_EUROS) return null;

    const value = row.rawByColumn[columnKey];
    if (typeof value !== "number") return null;

    const deviationEuros = roundToCents(value - typicalValue);
    if (Math.abs(deviationEuros) < DEVIATION_MINIMUM_EUROS) return null;

    const deviationPercent = (Math.abs(deviationEuros) / Math.abs(typicalValue)) * 100;
    if (deviationPercent < context.thresholdPercent) return null;

    const isCostRow = typeLabel === COST_TYPE_LABEL;
    const spendsOrEarnsMore = isCostRow ? value < typicalValue : value > typicalValue;
    const isWorseForResult = isCostRow ? spendsOrEarnsMore : !spendsOrEarnsMore;
    const deviation: MonthlyDeviation = {
        direction: spendsOrEarnsMore ? "up" : "down",
        impact: isWorseForResult ? "worse" : "better",
        value,
        typicalValue,
        deviationEuros,
        deviationPercent,
    };
    return deviation;
};

export const formatDeviationTooltip = (deviation: MonthlyDeviation, monthLabel: string) => {
    const sign = deviation.direction === "up" ? "+" : "-";
    const comparison = deviation.direction === "up" ? "acima do normal" : "abaixo do normal";
    const tooltip = `${monthLabel}: ${formatEuros(deviation.value)} · típico ${formatEuros(deviation.typicalValue)} · ${sign}${formatPercent(deviation.deviationPercent, 1)} ${comparison}`;
    return tooltip;
};
